import type { Size, SpatialObject, SpatialObjectKind } from '@/types';

/**
 * Static description of each object kind: what it can do and how it is sized.
 * Rendering is deliberately absent — components register renderers separately
 * (see components/objects/renderers), so the engine never imports React.
 */
export interface ObjectTypeDefinition {
  readonly kind: SpatialObjectKind;
  readonly label: string;
  readonly defaultSize: Size;
  readonly minSize: Size;
  readonly resizable: boolean;
  readonly rotatable: boolean;
  /** Whether this kind owns `childIds` and can be a parent. */
  readonly container: boolean;
  /** Kind-specific validation; returns human-readable problems. */
  readonly validate: (obj: SpatialObject) => readonly string[];
}

const SAFE_URL = /^https?:\/\//i;
const SAFE_IMAGE = /^(https:\/\/|data:image\/(png|jpe?g|gif|webp|avif|svg\+xml);)/i;
const none = (): readonly string[] => [];

const DEFINITIONS: Readonly<Record<SpatialObjectKind, ObjectTypeDefinition>> = {
  note: {
    kind: 'note',
    label: 'Note',
    defaultSize: { width: 240, height: 160 },
    minSize: { width: 120, height: 80 },
    resizable: true,
    rotatable: true,
    container: false,
    validate: (o) => (o.kind === 'note' && typeof o.content !== 'string' ? ['note content must be a string'] : []),
  },
  window: {
    kind: 'window',
    label: 'Window',
    defaultSize: { width: 420, height: 320 },
    minSize: { width: 240, height: 160 },
    resizable: true,
    rotatable: false,
    container: false,
    validate: (o) => (o.kind === 'window' && !o.applicationId ? ['window has no applicationId'] : []),
  },
  group: {
    kind: 'group',
    label: 'Group',
    defaultSize: { width: 320, height: 240 },
    minSize: { width: 80, height: 48 },
    resizable: true,
    rotatable: false,
    container: true,
    validate: none,
  },
  folder: {
    kind: 'folder',
    label: 'Folder',
    defaultSize: { width: 280, height: 200 },
    minSize: { width: 120, height: 96 },
    resizable: true,
    rotatable: false,
    container: true,
    validate: none,
  },
  file: {
    kind: 'file',
    label: 'File',
    defaultSize: { width: 160, height: 120 },
    minSize: { width: 96, height: 72 },
    resizable: true,
    rotatable: true,
    container: false,
    validate: (o) => {
      if (o.kind !== 'file') return [];
      const problems: string[] = [];
      if (!o.fileName) problems.push('file has no name');
      if (!(o.byteSize >= 0) || !Number.isFinite(o.byteSize)) problems.push('file size must be a non-negative number');
      return problems;
    },
  },
  terminal: {
    kind: 'terminal',
    label: 'Terminal',
    defaultSize: { width: 480, height: 300 },
    minSize: { width: 240, height: 120 },
    resizable: true,
    rotatable: false,
    container: false,
    validate: none,
  },
  bookmark: {
    kind: 'bookmark',
    label: 'Bookmark',
    defaultSize: { width: 260, height: 120 },
    minSize: { width: 160, height: 72 },
    resizable: true,
    rotatable: true,
    container: false,
    validate: (o) => (o.kind === 'bookmark' && !SAFE_URL.test(o.url) ? ['bookmark url must be http(s)'] : []),
  },
  image: {
    kind: 'image',
    label: 'Image',
    defaultSize: { width: 320, height: 240 },
    minSize: { width: 48, height: 48 },
    resizable: true,
    rotatable: true,
    container: false,
    validate: (o) => (o.kind === 'image' && !SAFE_IMAGE.test(o.src) ? ['image src must be https: or data:image'] : []),
  },
  application: {
    kind: 'application',
    label: 'Application',
    defaultSize: { width: 120, height: 120 },
    minSize: { width: 72, height: 72 },
    resizable: false,
    rotatable: false,
    container: false,
    validate: (o) => (o.kind === 'application' && !o.applicationId ? ['application has no applicationId'] : []),
  },
};

export const OBJECT_KINDS: readonly SpatialObjectKind[] = Object.keys(DEFINITIONS) as SpatialObjectKind[];

export const objectType = (kind: SpatialObjectKind): ObjectTypeDefinition => DEFINITIONS[kind];

export const isObjectKind = (value: unknown): value is SpatialObjectKind =>
  typeof value === 'string' && value in DEFINITIONS;
